import { css, cx } from 'styled-system/css';

import { getPaginationItems } from '@/components/atoms/Pagination/functions';
// Direct path, not the @/rsc/ui barrel: the barrel drags ParsedHTMLText (+ its CSS) into the island.
import { Text } from '@/rsc/ui/Text';

// Shared by the client islands that page through server-rendered article grids
// (ArticlesBrowser on /blog, AuthorArticlesBrowser on /authors/[slug]).

const MAX_PAGINATION_ITEMS = 7;

export type PaginationLabels = { prevPage: string; nextPage: string };

// atoms/LoadingSpinner (Chakra Spinner xl, 4px, 0.65s, violet.100 / violet.400)
const spinnerClass = css({
  display: 'inline-block',
  w: '3rem',
  h: '3rem',
  borderRadius: '99999px',
  borderWidth: '4px',
  borderStyle: 'solid',
  borderColor: 'violet.400',
  borderBottomColor: 'violet.100',
  borderInlineStartColor: 'violet.100',
  animation: 'sh-more-spin 0.65s linear infinite',
});

// atoms/Pagination (+ PaginationButton)
const pageButtonClass = css({
  p: { base: 0, xs: 2 },
  cursor: 'pointer',
  bg: 'transparent',
  _disabled: { cursor: 'initial', userSelect: 'none' },
});
const pageButtonTextClass = css({
  whiteSpace: { base: 'normal', xs: 'nowrap' },
  textDecoration: 'underline',
  color: 'text-dark.link',
  '[disabled] &': { color: 'transparent' },
});
const pageItemClass = css({
  w: '40px',
  h: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  color: 'bg-secondary-txt-hover',
  bg: 'transparent',
  transition: 'all 0.3s ease',
  fontWeight: 'medium',
});
const pageNumberClass = css({
  cursor: 'pointer',
  _hover: { bg: 'gray.100' },
  '&[aria-current=page]': { bg: 'lime.500', _hover: { bg: '#D9FC59' } },
});

type PaginationProps = {
  pageCount: number;
  currentPage: number;
  labels: PaginationLabels;
  onPageChange: (page: number) => void;
};

const PageButton = ({ text, active, onClick }: { text: string; active: boolean; onClick: () => void }) => (
  <button type="button" className={pageButtonClass} disabled={!active} onClick={onClick}>
    <Text
      as="span"
      variant={{ base: 'text-sm/lineHeight-5/font-medium', xs: 'text-md/lineHeight-6/font-medium' }}
      className={pageButtonTextClass}
    >
      {text}
    </Text>
  </button>
);

// v2 picked 1 or 2 side items with useBreakpointValue({ base: 1, md: 2 }); both rows render and
// CSS shows one (no resize listener, same markup before and after hydration).
const PageItems = ({
  pageCount,
  currentPage,
  sideItems,
  onPageChange,
  className,
}: {
  pageCount: number;
  currentPage: number;
  sideItems: number;
  onPageChange: (page: number) => void;
  className: string;
}) => (
  <div className={cx(css({ gap: { base: 3, sm: 4 }, mx: { base: 4, xs: 5 } }), className)}>
    {getPaginationItems(pageCount, currentPage, sideItems, MAX_PAGINATION_ITEMS).map((item, index) => {
      const isNumber = !isNaN(+item);
      if (!isNumber) {
        return (
          <span key={`pagination-item-${index}`} className={pageItemClass}>
            {item}
          </span>
        );
      }
      const isSelected = +item === currentPage;
      return (
        <button
          key={`pagination-item-${index}`}
          type="button"
          className={cx(pageItemClass, pageNumberClass)}
          aria-current={isSelected ? 'page' : undefined}
          onClick={() => onPageChange(+item)}
        >
          {item}
        </button>
      );
    })}
  </div>
);

export const Pagination = ({ pageCount, currentPage, labels, onPageChange }: PaginationProps) => (
  <nav aria-label="Pagination" className={css({ display: 'flex', alignItems: 'center' })}>
    <PageButton text={labels.prevPage} active={currentPage !== 1} onClick={() => onPageChange(currentPage - 1)} />
    <PageItems
      pageCount={pageCount}
      currentPage={currentPage}
      sideItems={1}
      onPageChange={onPageChange}
      className={css({ display: { base: 'flex', md: 'none' } })}
    />
    <PageItems
      pageCount={pageCount}
      currentPage={currentPage}
      sideItems={2}
      onPageChange={onPageChange}
      className={css({ display: { base: 'none', md: 'flex' } })}
    />
    <PageButton
      text={labels.nextPage}
      active={currentPage !== pageCount}
      onClick={() => onPageChange(currentPage + 1)}
    />
  </nav>
);

export const LoadingSpinner = () => (
  <div className={css({ display: 'flex', alignItems: 'center', justifyContent: 'center', minH: 'md' })}>
    <span className={spinnerClass} role="status">
      <span className={css({ srOnly: true })}>Loading...</span>
    </span>
  </div>
);
